/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kBrowserShared - the browser helpers the kEditableTable import graph needs, WITHOUT kalmstromShared4.
 *
 * WHY THIS FILE EXISTS (2026-10-14, owner decision "Decouple it properly"). kEditableTable and every
 * module it pulls in (kHTML, kModalDialog, kContextMenu, kStyles, kExcelExport, ...) used to import
 * `ks` from kalmstromShared4, and kalmstromShared4 runs `SetCoreUtils(ks)` at MODULE SCOPE. So a host
 * that has its own error reporter (WACommander: a static browser app outside SharePoint) had that
 * reporter REPLACED by the SharePoint handler the moment it loaded the table. Those modules now import
 * this file under the same local name (`import ks from "./kBrowserShared"`), so not one call site
 * changed, and nothing in their graph registers a handler.
 *
 * WHAT IT IS. A class extending the server-safe kCore (so Nz / Nb / N0 / KNotNullOrUndefined /
 * KNotEmptyString / GetRGBCodeFromHex ... are inherited), whose error gate and error handler are
 * whatever the HOST registered with SetCoreUtils:
 *   - KErrorMode           -> getCoreUtils().KErrorMode
 *   - KGlobalErrorHandler  -> getCoreUtils().KGlobalErrorHandler
 *   - KShowingErrorDialog  -> the registered handler's KShowingErrorDialog (false when it has none)
 * In every SharePoint product kalmstromShared4 registers `ks`, so these read and call `ks` exactly as
 * before. In a host that registered its own reporter they read and call that one.
 *
 * MOVED HERE from kalmstromShared4 (ks keeps the same public members and forwards to these):
 * DoCallBack, KNotEmptyHTML, PlainTextFromHTML, PlainTextFromHTMLWithDiagramToken, LocalCurrencyNumber,
 * NumberFromCurrency, CopyToClipboard.
 *
 * OPTIONAL HOOKS for what stays SharePoint-era in kalmstromShared4 (kalmstromShared4 installs both
 * when it loads; a host without it may install its own or leave them):
 *   - ShowNotificationHook   (null = ShowNotification does nothing)
 *   - AddDelayedActionHook   (null = AddDelayedAction runs the callback after a setTimeout)
 *
 * ⛔ THIS FILE MUST NEVER CALL SetCoreUtils, AND MUST NEVER IMPORT kalmstromShared4 - pinned by
 * tests/kBrowserShared.decouple.test.js.
 */
import { kCore, getCoreUtils } from "../kReusableCoreTS/kCoreHandler";

declare global {
    interface Window {
        KDebug: any;
    }
}

// The same console hook kalmstromShared4 creates (kStyles writes window.KDebug.kStyles). Created
// only when absent, so whichever of the two loads first makes it and the other keeps it.
if (typeof window !== "undefined" && undefined === window.KDebug) {
    window.KDebug = {};
    window.KDebug.Phrases = [];
}

export default class kBrowserShared extends kCore {
    /** The host's error gate (getCoreUtils().KErrorMode). */
    public static get KErrorMode(): boolean {
        return getCoreUtils().KErrorMode === true;
    }

    /** True while the registered handler shows its error dialog (ks does; a handler without the
        member answers false). Read by the dialog code so the error dialog itself can still be built. */
    public static get KShowingErrorDialog(): boolean {
        const Handler: any = getCoreUtils();
        return Handler.KShowingErrorDialog === true;
    }

    /** The host's error handler (getCoreUtils().KGlobalErrorHandler).
        DOCUMENTED EXCEPTION: no error-mode guard and no try/catch - this IS the call to the central
        handler (a guard would drop every report made after the first, and a catch could only call
        the same handler again). */
    public static KGlobalErrorHandler = (e: any, Location: string, AdditionalMessage?: string): void => {
        getCoreUtils().KGlobalErrorHandler(e, Location, AdditionalMessage);
    };

    /** Optional: shows a notification. kalmstromShared4 installs ks.ShowNotification. */
    public static ShowNotificationHook: ((Message: string, AutoCloseDelay?: number, IsWarning?: boolean) => void) | null = null;

    /** Optional: queues a delayed action. kalmstromShared4 installs ks.AddDelayedAction. */
    public static AddDelayedActionHook: ((NewCategory: string, CallBack: any, Comment?: string, MSDelay?: number, Params?: any) => void) | null = null;

    public static ShowNotification = (Message: string, AutoCloseDelay?: number, IsWarning?: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (kBrowserShared.ShowNotificationHook !== null) {
                kBrowserShared.ShowNotificationHook(Message, AutoCloseDelay, IsWarning);
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.ShowNotification"); }
    };

    public static AddDelayedAction = (
        NewCategory: string,
        CallBack: any,
        Comment?: string,
        MSDelay?: number,
        Params?: any
    ): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (kBrowserShared.AddDelayedActionHook !== null) {
                kBrowserShared.AddDelayedActionHook(NewCategory, CallBack, Comment, MSDelay, Params);
                return;
            }
            window.setTimeout(kBrowserShared.RunDelayed, kBrowserShared.N0(MSDelay), CallBack, Params);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.AddDelayedAction"); }
    };

    private static RunDelayed = (CallBack: any, Params: any): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            kBrowserShared.DoCallBack(CallBack, Params);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.RunDelayed"); }
    };

    public static DoCallBack = (
        Callback: any | null,
        Params?: any,
        MakeCBNull?: boolean
    ): void => {
        if (getCoreUtils().KErrorMode === true && kBrowserShared.KShowingErrorDialog === false) {
            return;
        }
        try {
            if (typeof Callback === "function") {
                if (kBrowserShared.KNotNullOrUndefined(Params)) {
                    Callback(Params);
                } else {
                    Callback();
                }
            }
            if (MakeCBNull !== false) {
                Callback = null;
            }
            Params = null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.DoCallBack");
        }
    };

    public static KNotEmptyHTML = (StringToCheck: string): boolean => {
        if (getCoreUtils().KErrorMode === true) {
            return false;
        }
        try {
            if (kBrowserShared.KNotEmptyString(StringToCheck) === true) {
                if (kBrowserShared.KNotEmptyString(StringToCheck.trim())) {
                    const PlainText: string = kBrowserShared
                        .PlainTextFromHTML(StringToCheck)
                        .trim();
                    if (kBrowserShared.KNotEmptyString(PlainText)) {
                        return true; //we have valid content
                    }
                }
            }
            return false; //Empty html
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.KNotEmptyHTML");
            return false;
        }
    };

    public static PlainTextFromHTML = (HTMLString: string): string => {
        if (getCoreUtils().KErrorMode === true) {
            return "";
        }
        try {
            if (kBrowserShared.KNotEmptyString(HTMLString) === true) {
                const TestDiv: HTMLDivElement = document.createElement("div");
                TestDiv.innerHTML = HTMLString;
                return TestDiv.innerText.trim();
            }
            return ""; //Empty html
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.PlainTextFromHTML");
            return "";
        }
    };

    // PlainTextFromHTML for content that may carry inserted diagrams (an <svg> or the .k-mermaid
    // wrapper the rich editor's Insert buttons produce). Each one is replaced by DiagramToken
    // (e.g. "[" + LNG.DIAGRAM + "]") before the text is extracted.
    public static PlainTextFromHTMLWithDiagramToken = (
        HTMLString: string, DiagramToken: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (kBrowserShared.KNotEmptyString(HTMLString) === false) { return ""; }
            const TestDiv: HTMLDivElement = document.createElement("div");
            TestDiv.innerHTML = HTMLString;
            const Wraps: NodeListOf<Element> = TestDiv.querySelectorAll(".k-mermaid");
            for (let I: number = 0; I < Wraps.length; I = I + 1) {
                Wraps[I].replaceWith(document.createTextNode(" " + DiagramToken + " "));
            }
            const Svgs: NodeListOf<Element> = TestDiv.querySelectorAll("svg");
            for (let I: number = 0; I < Svgs.length; I = I + 1) {
                Svgs[I].replaceWith(document.createTextNode(" " + DiagramToken + " "));
            }
            return TestDiv.innerText.replace(/\s+/g, " ").trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.PlainTextFromHTMLWithDiagramToken");
            return kBrowserShared.PlainTextFromHTML(HTMLString);
        }
    };

    public static LocalCurrencyNumber = (Nr: number): string => {
        if (getCoreUtils().KErrorMode === true) {
            return "";
        }
        try {
            if (kCore.NumberandDateFormat === null) {
                kCore.GetLocalDataFormat(navigator.language.toLowerCase());
            }
            if (kCore.NumberandDateFormat) {
                return Nr.toLocaleString(kCore.NumberandDateFormat.BC47, { style: "currency", "currency": kCore.NumberandDateFormat.Curr });
            } else {
                return Nr.toString();
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.LocalCurrencyNumber");
            return "";
        }
    };

    public static NumberFromCurrency = (FormattedNumber: string): number => {
        if (getCoreUtils().KErrorMode === true) {
            return 0;
        }
        try {
            if (kCore.NumberandDateFormat === null) {
                kCore.GetLocalDataFormat(navigator.language.toLowerCase());
            }
            if (kCore.NumberandDateFormat) {
                const Zero: number = 0;
                const FormattedZero: string = Zero.toLocaleString(kCore.NumberandDateFormat.BC47, { style: "currency", "currency": kCore.NumberandDateFormat.Curr });
                const CurrencySymbol = FormattedZero.replace("0", "").replace(".00", "").replace(",00", "");
                let NumberString: string = "";
                if (typeof (FormattedNumber) === "string") {
                    NumberString = FormattedNumber.replace(CurrencySymbol, "");
                } else {
                    NumberString = kBrowserShared.Nz(FormattedNumber);
                }
                return kBrowserShared.N0(NumberString);
            } else {
                return 0;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.NumberFromCurrency");
            return 0;
        }
    };

    // Put text on the clipboard. RETURNS void, not a Promise (the workspace bans async/await); the
    // clipboard API's promise is consumed here with NAMED handlers.
    public static CopyToClipboard = (textToCopy: string,
        OnCopied: (() => void) | null = null,
        OnFailed: ((Reason: unknown) => void) | null = null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // Navigator clipboard api needs a secure context (https)
            const MyNav: any = navigator as any;
            if (MyNav.clipboard && window.isSecureContext) {
                if (OnCopied === null && OnFailed === null) {
                    MyNav.clipboard.writeText(textToCopy).then(
                        kBrowserShared.ClipboardWriteDone,
                        kBrowserShared.ClipboardWriteFailed
                    );
                    return;
                }
                let Success: () => void = kBrowserShared.ClipboardWriteDone;
                let Failure: (Reason: unknown) => void = kBrowserShared.ClipboardWriteFailed;
                if (OnCopied !== null) { Success = OnCopied; }
                if (OnFailed !== null) { Failure = OnFailed; }
                MyNav.clipboard.writeText(textToCopy).then(Success).catch(Failure);
                return;
            }
            kBrowserShared.CopyViaTextArea(textToCopy, OnCopied, OnFailed);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: explicit OnFailed owns expected clipboard refusal/recovery; absent a hook, report centrally below. Never report both.
            if (OnFailed !== null) {
                OnFailed(e);
            } else {
                getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.CopyToClipboard");
            }
        }
    };

    private static ClipboardWriteDone = (): void => {
        // A successful clipboard write needs no action. This exists only so the .then() above
        // takes a NAMED handler rather than an anonymous callback.
    };

    // DOCUMENTED EXCEPTION: no try/catch - the whole body is the call to the central handler.
    private static ClipboardWriteFailed = (Reason: any): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        getCoreUtils().KGlobalErrorHandler(Reason, "kBrowserShared.CopyToClipboard");
    };

    // Fallback for an insecure context or a browser without navigator.clipboard: select the text
    // in an off-screen textarea and use the legacy execCommand copy.
    private static CopyViaTextArea = (textToCopy: string,
        OnCopied: (() => void) | null,
        OnFailed: ((Reason: unknown) => void) | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const textArea: HTMLTextAreaElement = document.createElement("textarea");
            textArea.value = textToCopy;

            // Move textarea out of the viewport so it's not visible (CSSOM, allowed under a strict CSP)
            textArea.style.position = "absolute";
            textArea.style.left = "-999999px";

            document.body.appendChild(textArea);
            textArea.select();

            const Copied: boolean = document.execCommand('copy');
            textArea.remove();
            if (Copied === true) {
                if (OnCopied !== null) {
                    let Failure: (Reason: unknown) => void = kBrowserShared.ClipboardWriteFailed;
                    if (OnFailed !== null) { Failure = OnFailed; }
                    Promise.resolve().then(OnCopied).catch(Failure);
                }
            } else if (OnFailed !== null) {
                OnFailed(new Error("Clipboard copy command returned false"));
            }
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: explicit OnFailed owns fallback failure; absent a hook, report centrally below.
            if (OnFailed !== null) {
                OnFailed(e);
            } else {
                getCoreUtils().KGlobalErrorHandler(e, "kBrowserShared.CopyViaTextArea");
            }
        }
    };
}
